import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import Header from './components/Header';
import Hero from './components/Hero';
import Services from './components/Services';
import About from './components/About';
import Portfolio from './components/Portfolio';
import Contact from './components/Contact';
import Footer from './components/Footer';
import FilmGallery from './components/FilmGallery';
import { PrivacyPolicy, TermsOfService, AccessibilityStatement } from './components/Legal';
import './App.css';

const PageWrapper = ({ children, title, description, image, heading }) => {
  const location = useLocation();
  const canonicalUrl = `https://sandstormflix.com${location.pathname === '/' ? '' : location.pathname}`;
  const siteTitle = title ? `${title} | Sandstorm Flix` : 'Sandstorm Flix | Cinematic Storytelling';
  const siteDesc = description || "Visionary storytelling by Kevin V. Tudor. Exploring the depths of the human experience through cinema.";
  
  // Default to the 1200x630 branded raster image
  const siteImage = image || "https://images.unsplash.com/photo-1478720568477-152d9b164e26?auto=format&fit=crop&w=1200&h=630&q=80";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="w-full relative"
    >
      <Helmet>
        {/* Basic Metadata */}
        <title>{siteTitle}</title>
        <meta name="description" content={siteDesc} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* OpenGraph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={siteTitle} />
        <meta property="og:description" content={siteDesc} />
        <meta property="og:image" content={siteImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={canonicalUrl} />
        <meta property="twitter:title" content={siteTitle} />
        <meta property="twitter:description" content={siteDesc} />
        <meta property="twitter:image" content={siteImage} />
      </Helmet>
      <h1 className="sr-only">{heading || title || 'Sandstorm Flix cinematic storytelling'}</h1>
      {children}
    </motion.div>
  );
};

function AppContent() {
  const location = useLocation();
  
  return (
    <div className="relative min-h-screen w-full bg-[#FDFCF8] selection:bg-accent-orange/20 selection:text-black overflow-x-hidden">
      <Header />
      <main className="pt-20 w-full overflow-x-hidden relative">
        <AnimatePresence mode="wait" initial={false}>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={
              <PageWrapper title="Home" heading="Cinematic Storytelling by Sandstorm Flix" description="Sandstorm Flix: Visionary storytelling by Kevin V. Tudor. Award-winning films, psychological thrillers, and cinematic shorts.">
                <Hero />
                <div className="space-y-0">
                  <Services />
                  <About />
                  <Portfolio />
                  <Contact />
                </div>
              </PageWrapper>
            } />
            
            <Route path="/tinman" element={
              <PageWrapper 
                title="Tinman" 
                description="Uncover a chilling 1950s conspiracy in Tinman. A doctor races against time to expose a man-made virus weaponized for population control."
                image="https://res.cloudinary.com/dwumujtpu/image/upload/v1774568823/tinman_preview_iqebch.png"
              >
                <FilmGallery title="Tinman" category="Sci-Fi Feature" />
              </PageWrapper>
            } />
            <Route path="/playground" element={
              <PageWrapper 
                title="The Playground" 
                description="Where innocence meets terror. The Playground is a haunting psychological thriller exploring the dark, forgotten echoes of childhood."
                image="https://res.cloudinary.com/dwumujtpu/image/upload/v1774579065/play-magazine-cover_ijcktx.jpg"
              >
                <FilmGallery title="The Playground" category="Horror Thriller" />
              </PageWrapper>
            } />
            <Route path="/festivals" element={
              <PageWrapper 
                title="The Festivals" 
                description="From Cannes to Sundance: Explore the award-winning journey of Sandstorm Flix across the world's most prestigious film festivals."
                image="https://res.cloudinary.com/dwumujtpu/image/upload/v1774577647/6484dc164cc0f_cannes-sign-lights_uzz48s.jpg"
              >
                <FilmGallery title="The Festivals" category="Awards & Press" />
              </PageWrapper>
            } />
            <Route path="/fairytales" element={
              <PageWrapper 
                title="Truth and Fairytales" 
                description="A raw, visceral deconstruction of grief. Follow a psychologist's descent through modern myths in the aftermath of a devastating pandemic."
                image="https://res.cloudinary.com/dwumujtpu/image/upload/v1774579862/truth-and-fairytales-guy-in-the-dark_hdfhwf.jpg"
              >
                <FilmGallery title="Truth and Fairytales" category="Dramatic Series" />
              </PageWrapper>
            } />
            <Route path="/shorts" element={
              <PageWrapper 
                title="YouTube Shorts" 
                description="Bite-sized cinema, massive impact. Discover visionary micro-narratives that push the boundaries of digital storytelling by Kevin V. Tudor."
                image="https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1200&q=80"
              >
                <FilmGallery title="YouTube Shorts" category="Digital Content" />
              </PageWrapper>
            } />
            <Route path="/print" element={
              <PageWrapper 
                title="In Print" 
                description="Leading global publications feature the cinematic vision of Kevin V. Tudor and the rise of Sandstorm Flix."
                image="https://res.cloudinary.com/dwumujtpu/image/upload/v1774580360/64bae5b810ec2_doc-newspaper_vqhw5a.jpg"
              >
                <FilmGallery title="In Print" category="Media Coverage" />
              </PageWrapper>
            } />
            <Route path="/team" element={
              <PageWrapper 
                title="The Team" 
                description="The architects of imagination. Meet the award-winning visionaries, cinematographers, and creators behind the lens at Sandstorm Flix."
                image="https://res.cloudinary.com/dwumujtpu/image/upload/v1774632757/who-we-are-group-in-yellow-sofa_pmk6lb.jpg"
              >
                <FilmGallery title="The Team" category="Production Crew" />
              </PageWrapper>
            } />
            
            <Route path="/about" element={<PageWrapper title="About Us" description="Learn about the cinematic legacy of Kevin V. Tudor and the mission of Sandstorm Flix."><About /></PageWrapper>} />
            <Route path="/contact" element={<PageWrapper title="Contact" description="Get in touch with Sandstorm Flix for collaborations, press inquiries, or project bookings."><Contact /></PageWrapper>} />
            <Route path="/privacy" element={<PageWrapper title="Privacy Policy"><PrivacyPolicy /></PageWrapper>} />
            <Route path="/terms" element={<PageWrapper title="Terms of Service"><TermsOfService /></PageWrapper>} />
            <Route path="/accessibility" element={<PageWrapper title="Accessibility"><AccessibilityStatement /></PageWrapper>} />
            
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AnimatePresence>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;